import React, { type FC, type ReactNode, type PropsWithChildren } from 'react';

import styled from '@emotion/styled';

export interface BreadcrumbProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** List of items that make up the breadcrumb path. */
  items: ReactNode[];
  /** Sets the seperator indicator for the breadcrumb path. */
  seperator?: ReactNode | string;
}

const BreadcrumbWrapper = styled.li(
  {
    label: 'breadcrumbWrapper',
    padding: 0,
    margin: 0,
    listStyleType: 'none',
    display: 'inline-block',
    '&:hover': {
      opacity: 1,
    },
  },
  ({ theme }) => ({
    '& > a': {
      color: theme.colors.coolGrey[70],
      textDecoration: 'none',
    },
  }),
);

const BreadcrumbSeparator = styled.span(({ theme }) => ({
  label: 'breadcrumbSeparator',
  color: theme.colors.coolGrey[100],
  cursor: 'default',
  fontSize: '1em',
  margin: `0 ${theme.space.s8}px`,
}));

const BreadcrumbContent = styled.ul(({ theme }) => ({
  color: theme.colors.coolGrey[100],
  label: 'breadcrumbContent',
  margin: 0,
  padding: 0,
  '& > a': {
    color: theme.colors.coolGrey[100],
    textDecoration: 'none',
  },
}));

const Breadcrumb: FC<PropsWithChildren<BreadcrumbProps>> = ({ className, items, seperator = '/' }) => {
  const getCrumbs = () => {
    const crumbs: ReactNode[] = items.map((item, index) => {
      const seperatorElement = index !== items.length - 1 && (
        <React.Fragment key={`seperator-${index}`}>
          <BreadcrumbSeparator>{seperator}</BreadcrumbSeparator>
        </React.Fragment>
      );

      const crumb = (
        <React.Fragment key={`fragment-${index}`}>
          <BreadcrumbWrapper key={index}>
            {item}
            {seperatorElement}
          </BreadcrumbWrapper>
        </React.Fragment>
      );

      return crumb;
    });

    return crumbs;
  };

  return (
    <nav aria-label="breadcrumb" className={className}>
      <BreadcrumbContent>{getCrumbs()}</BreadcrumbContent>
    </nav>
  );
};

export { Breadcrumb };
export default Breadcrumb;
